import { useState, useCallback } from 'react'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
} from '@/utils/tour-styles'

interface TourStep {
    selector: string
    title: string
    description: string
}

const TOUR_STEPS: TourStep[] = [
    {
        selector: '[data-tour="sp-filters"]',
        title: 'Bộ lọc báo cáo',
        description:
            'Lọc dữ liệu theo:\n• Khoảng thời gian (tháng)\n• Nhân viên Sales\n• Nhóm sản phẩm\n• Quy mô công ty',
    },
    {
        selector: '[data-tour="sp-summary-cards"]',
        title: 'Thống kê tổng quan',
        description:
            'Hiển thị các chỉ số chính:\n• Tổng số khách hàng\n• Khách hàng mới trong kỳ\n• Top ngành nghề\n• Top dịch vụ quan tâm',
    },
    {
        selector: '[data-tour="sp-industry-radar"]',
        title: 'Biểu đồ ngành nghề',
        description:
            'Radar chart phân bố khách hàng theo ngành.\n• Hiển thị top ngành nghề\n• So sánh tỷ trọng các ngành\n• Giúp nhận diện xu hướng thị trường',
    },
    {
        selector: '[data-tour="sp-daily-input"]',
        title: 'Input hàng ngày',
        description:
            'Biểu đồ theo dõi hoạt động Sales:\n• Số lượng liên hệ mới mỗi ngày\n• Xu hướng tăng/giảm\n• So sánh với kỳ trước',
    },
    {
        selector: '[data-tour="sp-employee-contacts"]',
        title: 'Bảng liên hệ theo nhân viên',
        description:
            'Chi tiết số lượng liên hệ của từng nhân viên:\n• Phân loại theo trạng thái\n• So sánh với kỳ trước\n• Tính % tăng trưởng',
    },
    {
        selector: '[data-tour="sp-conversion-funnel"]',
        title: 'Phễu chuyển đổi',
        description:
            'Biểu đồ phễu chuyển đổi khách hàng:\n• Từ Lead đến Active\n• Tỷ lệ chuyển đổi từng giai đoạn\n• Nhận diện điểm nghẽn',
    },
    {
        selector: '[data-tour="sp-pipeline-overview"]',
        title: 'Tổng quan Pipeline',
        description:
            'Biểu đồ tròn phân bố khách hàng:\n• Theo từng trạng thái\n• Tỷ lệ phần trăm\n• Click để xem chi tiết',
    },
    {
        selector: '[data-tour="sp-product-bar"]',
        title: 'Thống kê theo sản phẩm',
        description:
            'Biểu đồ cột sản phẩm được quan tâm:\n• Top sản phẩm theo số lượng khách\n• So sánh giữa các nhóm\n• Giúp định hướng bán hàng',
    },
    {
        selector: '[data-tour="sp-best-employees"]',
        title: 'Top nhân viên Sales',
        description:
            'Bảng xếp hạng nhân viên:\n• Theo số khách hàng mới\n• Theo chuyển đổi thành công\n• Hiển thị khi không lọc nhân viên',
    },
    {
        selector: '[data-tour="sp-status-transitions"]',
        title: 'Chuyển đổi trạng thái',
        description:
            'Biểu đồ dòng chảy trạng thái:\n• Từ trạng thái nào đến trạng thái nào\n• Số lượng chuyển đổi\n• Thời gian trung bình',
    },
]

export const useSalePerformancesTour = () => {
    const [isActive, setIsActive] = useState(false)
    const [currentStep, setCurrentStep] = useState(0)

    const getVisibleSteps = useCallback(() => {
        return TOUR_STEPS.filter((step) => {
            const element = document.querySelector(step.selector)
            return element !== null
        })
    }, [])

    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
        setCurrentStep(0)
    }, [])

    const showTourStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            injectTourStyles()
            cleanupTourElements()
            createTourOverlay()

            const step = steps[stepIndex]
            if (!step) return

            const element = document.querySelector(step.selector) as HTMLElement
            if (!element) {
                if (stepIndex < steps.length - 1) {
                    showTourStep(stepIndex + 1, steps)
                } else {
                    endTour()
                }
                return
            }

            element.classList.add('tour-highlight-element')
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })

            setTimeout(() => {
                const isLastStep = stepIndex === steps.length - 1
                const popover = createTourPopover({
                    title: step.title,
                    description: step.description,
                    currentStep: stepIndex + 1,
                    totalSteps: steps.length,
                    onPrev:
                        stepIndex > 0
                            ? () => goToStep(stepIndex - 1, steps)
                            : undefined,
                    onNext: isLastStep
                        ? endTour
                        : () => goToStep(stepIndex + 1, steps),
                    onClose: endTour,
                    isLastStep,
                })

                document.body.appendChild(popover)
                positionPopover(popover, element)
            }, 300)
        },
        [endTour]
    )

    const goToStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            setCurrentStep(stepIndex)
            showTourStep(stepIndex, steps)
        },
        [showTourStep]
    )

    const startTour = useCallback(() => {
        const visibleSteps = getVisibleSteps()
        if (visibleSteps.length === 0) {
            console.warn('No visible elements to tour')
            return
        }

        setIsActive(true)
        setCurrentStep(0)
        showTourStep(0, visibleSteps)
    }, [getVisibleSteps, showTourStep])

    return {
        isActive,
        currentStep,
        startTour,
        endTour,
        totalSteps: getVisibleSteps().length,
    }
}

export default useSalePerformancesTour
